import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Row, Col, Table, Input, Select, Button, Typography, Space, Dropdown, Tag, Spin } from 'antd';
import { PlusOutlined, DownOutlined, LoadingOutlined } from '@ant-design/icons';
import { Modal, Field } from './SharedUI';
import httpInjectorService from 'services/http-injector.service';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

/* Strip markdown-style [text](url) if it ever comes through that way */
function cleanWebsite(url) {
  if (!url) return '';
  const match = String(url).match(/\((https?:\/\/[^)]+)\)/);
  return match ? match[1] : url;
}

/* ──────────────────────────────────────────────────────────────── */
/* ORGANIZATIONS VIEW                                               */
/* ──────────────────────────────────────────────────────────────── */
export function OrganizationsView({ notify, onOpenOrg, setOrgsCache }) {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [modal, setModal] = useState(null); // { mode: 'create'|'edit', org }
  const [saving, setSaving] = useState(false);

  // Plans
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [assigningId, setAssigningId] = useState(null); // org.id currently being assigned

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await httpInjectorService.listorganization();
      // API shape: { status: 'success', data: [ { id, org_name, company_website, email,
      //   employee_count, plan_id, plan_name, plan_type, plan_code }, ... ] }
      setOrgs(res.data || []);
    } catch (e) {
      notify?.(e.message || 'Could not load organizations', 'error');
    } finally { setLoading(false); }
  }, [notify]);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const res = await httpInjectorService.getSubscriptionPlans();
      setPlans(res.plans || res.data || []);
    } catch (e) {
      notify?.(e.message || 'Could not load plans', 'error');
    } finally { setPlansLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPlans(); }, [loadPlans]);
  useEffect(() => { setOrgsCache?.(orgs); }, [orgs]);

  const filteredOrgs = useMemo(() => {
    const q = searchText.toLowerCase();
    return orgs.filter(o =>
      o.org_name?.toLowerCase().includes(q)
      || o.email?.toLowerCase().includes(q)
      || String(o.id).includes(q));
  }, [orgs, searchText]);

  const submitOrg = async (form) => {
    setSaving(true);
    try {
      const fn = modal.mode === 'create' ? httpInjectorService.createOrganization : httpInjectorService.updateOrganization;
      await fn(form);
      notify?.(modal.mode === 'create' ? 'Organization created' : 'Organization updated');
      await load();
      setModal(null);
    } catch (e) {
      notify?.(e.message || 'Something went wrong', 'error');
    } finally { setSaving(false); }
  };

  const assignPlan = async (orgId, planId) => {
    if (!planId) return;
    setAssigningId(orgId);
    try {
      // Rename `assignPlan` to match your actual "assign plan" API method
      await httpInjectorService.assignPlan({ org_id: orgId, plan_id: planId });
      notify?.('Plan assigned');
      await load();
    } catch (e) {
      notify?.(e.message || 'Could not assign plan', 'error');
    } finally {
      setAssigningId(null);
    }
  };

  /* ── Table column definitions ─────────────────────────────────── */
  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Organization',
      dataIndex: 'org_name',
      key: 'org_name',
      sorter: (a, b) => String(a.org_name || '').localeCompare(String(b.org_name || '')),
      render: (text, record) => (
        <Button
          type="link"
          style={{ padding: 0, fontWeight: 600 }}
          onClick={() => onOpenOrg?.(record.id)}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Website',
      dataIndex: 'company_website',
      key: 'company_website',
      render: (url) => {
        const website = cleanWebsite(url);
        return website
          ? <span title={website}>{website}</span>
          : <span style={{ color: '#bfbfbf' }}>—</span>;
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email) => email
        ? <span title={email}>{email}</span>
        : <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: 'Employees',
      dataIndex: 'employee_count',
      key: 'employee_count',
      width: 110,
      sorter: (a, b) => (a.employee_count || 0) - (b.employee_count || 0),
    },
   {
  title: 'Plan',
  key: 'plan',
  width: 200,
  render: (_, record) => {
    const isAssigning = assigningId === record.id;

    if (isAssigning) {
      return (
        <Space size={6}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 14 }} spin />} />
          <span style={{ color: '#8c8c8c' }}>Assigning…</span>
        </Space>
      );
    }

    return (
      <Select
        style={{ width: '100%' }}
        placeholder={plansLoading ? 'Loading plans…' : plans.length === 0 ? 'No plans available' : 'Assign a plan…'}
        disabled={plansLoading || plans.length === 0}
        onChange={(value) => assignPlan(record.id, value)}
        value={record.plan_id ?? undefined}
        allowClear
        onClear={() => assignPlan(record.id, null)}
      >
        {plans.map(p => (
          <Option key={p.id} value={p.id}>{p.plan_name || p.name}</Option>
        ))}
      </Select>
    );
  },
},
    {
      title: 'Actions',
      key: 'actions',
      width: 110,
      align: 'right',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              { key: 'edit', label: 'Edit' },
            ],
            onClick: ({ key }) => {
              if (key === 'edit') setModal({ mode: 'edit', org: record });
            },
          }}
          trigger={['click']}
        >
          <Button type="link" onClick={(e) => e.preventDefault()}>
            Actions <DownOutlined />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <Card style={{ marginTop: '50px' }}>
      {/* Header Row */}
      <Row justify="space-between" align="middle">
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            📋 Organization Directory
          </Title>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModal({ mode: 'create', org: null })}
          >
            Create Organization
          </Button>
        </Col>
      </Row>

      {/* Search and Row Count Filter */}
      <Row style={{ marginTop: 20 }}>
        <Col span={24}>
          <div style={{ display: 'flex', gap: '16px' }}>
            <Search
              placeholder="Search organizations"
              allowClear
              onSearch={(value) => setSearchText(value)}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '300px' }}
            />
            <Select
              defaultValue={10}
              style={{ width: 120 }}
              onChange={(value) => setPageSize(value)}
            >
              <Option value={10}>10 rows</Option>
              <Option value={25}>25 rows</Option>
              <Option value={50}>50 rows</Option>
            </Select>
          </div>
        </Col>
      </Row>

      {/* Table */}
      <div style={{ position: 'relative', zIndex: 0 }}>
        <Table
          columns={columns}
          dataSource={filteredOrgs}
          rowKey="id"
          bordered
          loading={loading}
          pagination={{ pageSize }}
          size="middle"
          scroll={{ x: 1300 }}
          style={{ marginTop: '20px' }}
        />
      </div>

      {modal && (
        <OrganizationModal
          mode={modal.mode}
          org={modal.org}
          saving={saving}
          onClose={() => setModal(null)}
          onSubmit={submitOrg}
        />
      )}
    </Card>
  );
}

function OrganizationModal({ mode, org, saving, onClose, onSubmit }) {
  const [form, setForm] = useState(() => org
    ? {
        id: org.id,
        org_name: org.org_name,
        employee_count: org.employee_count ?? 0,
        company_website: org.company_website ?? '',
        email: org.email ?? '',
      }
    : { org_name: '', employee_count: 0, company_website: '', email: '' });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Modal title={mode === 'create' ? 'Create organization' : `Edit ${org.org_name}`} onClose={onClose}>
      <Field label="Organization name">
        <input className="sa-input w-full px-3 py-2 text-sm" value={form.org_name} onChange={e => set('org_name', e.target.value)} placeholder="e.g. Acme Corp" />
      </Field>
      <Field label="Website">
        <input className="sa-input w-full px-3 py-2 text-sm" value={form.company_website} onChange={e => set('company_website', e.target.value)} placeholder="e.g. www.acme.com" />
      </Field>
      <Field label="Email">
        <input type="email" className="sa-input w-full px-3 py-2 text-sm" value={form.email} onChange={e => set('email', e.target.value)} placeholder="e.g. contact@acme.com" />
      </Field>
      <Field label="Employee count">
        <input
          type="number"
          min="0"
          className="sa-input w-full px-3 py-2 text-sm"
          value={form.employee_count}
          onChange={e => set('employee_count', Number(e.target.value))}
          placeholder="0"
        />
      </Field>
      <div className="flex justify-end gap-2 mt-2">
        <button className="sa-btn sa-btn-ghost px-4 py-2 text-sm" onClick={onClose}>Cancel</button>
        <button
          className="sa-btn sa-btn-primary px-4 py-2 text-sm"
          disabled={saving || !form.org_name}
          onClick={() => onSubmit(form)}
        >
          {saving ? 'Saving…' : mode === 'create' ? 'Create organization' : 'Save changes'}
        </button>
      </div>
    </Modal>
  );
}